/**
 * Backend Analytics Service
 * Handles API calls to backend analytics/tracking endpoints
 * Base URL: COMMUNITIES_SERVER_URL
 */

import { BaseService } from '@common/http-layer/service/base.service'

interface TrackEventPayload {
  eventName: string
  locationId: string
  contactId: string
  groupId?: string
  eventData: Record<string, unknown>
}

export class BackendAnalyticsService extends BaseService {
  constructor() {
    super({
      baseURL: 'COMMUNITIES_SERVER_URL',
      basePath: '/analytics', // COMMUNITIES_SERVER_URL already includes /communities
    })
  }

  /**
   * Track event to backend Elasticsearch
   */
  async trackEvent(payload: TrackEventPayload): Promise<void> {
    try {
      await this.post('/track-event', payload)
    } catch (error) {
      // Silently fail - analytics should not break the app
      console.error('[BackendAnalyticsService] Track event failed:', error)
    }
  }

  /**
   * Track event to Elasticsearch with specific index and status
   * (For future use when we need structured logging)
   */
  async trackEventV2(payload: {
    indexName: string
    locationId: string
    contactId: string
    status: 'success' | 'error' | 'skipped' | 'failed'
    logLevel: 'info' | 'warn' | 'error'
    eventData: Record<string, unknown>
  }): Promise<void> {
    try {
      const eventName = `client-portal-${payload.indexName}-`

      const trackEvent = {
        eventName,
        locationId: payload.locationId,
        contactId: payload.contactId,
        eventData: {
          status: payload.status,
          logLevel: payload.logLevel,
          ...payload.eventData,
        },
      }

      await this.post('/track-event', trackEvent)
    } catch (error) {
      console.error('[BackendAnalyticsService] TrackEventV2 failed:', error)
    }
  }
}

// Export singleton instance
export const backendAnalyticsService = new BackendAnalyticsService()
